import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 定数 ---

/**
 * 集計軸（API4142）。
 * LABEL=品目ラベル別 / STORE=店舗別 / MONTH=月別 / YEAR=年別。
 */
export const ExpenseAggregateAxis = {
  LABEL: '品目ラベル別',
  STORE: '店舗別',
  MONTH: '月別',
  YEAR: '年別',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ExpenseAggregateAxis = keyof typeof ExpenseAggregateAxis;

/**
 * 経費名候補検索のマッチ種別（API4141）。
 * PREFIX=前方一致 / SUFFIX=後方一致。省略時は前方 OR 後方の両方（BE既定）。
 */
export type ExpenseSuggestionMatchType = 'PREFIX' | 'SUFFIX';

// --- 型定義（品目ラベル）---

export type ExpenseLabel = {
  expenseLabelId: string;
  name: string;
  /** HEX 色コード（#RRGGBB / #RRGGBBAA） */
  color: string;
};

export type CreateExpenseLabelParams = {
  name: string;
  color: string;
};

export type UpdateExpenseLabelParams = {
  expenseLabelId: string;
  name: string;
  color: string;
};

export type DeleteExpenseLabelParams = {
  expenseLabelId: string;
};

// --- 型定義（店舗）---

export type ExpenseStore = {
  expenseStoreId: string;
  name: string;
  /** デフォルト品目ラベルID（未設定は null） */
  defaultLabelId: string | null;
  /** インボイス番号 T+13桁（未設定は null） */
  invoiceNumber: string | null;
};

export type CreateExpenseStoreParams = {
  name: string;
  defaultLabelId?: string;
  invoiceNumber?: string;
};

export type UpdateExpenseStoreParams = {
  expenseStoreId: string;
  name: string;
  defaultLabelId?: string;
  invoiceNumber?: string;
};

export type DeleteExpenseStoreParams = {
  expenseStoreId: string;
};

// --- 型定義（経費）---

export type CreateExpenseParams = {
  /** 購入日（format: yyyy-MM-dd） */
  purchasedOn: string;
  name: string;
  amount: number;
  quantity: number;
  storeId: string;
  labelId: string;
  note?: string;
};

export type UpdateExpenseParams = CreateExpenseParams & {
  expenseId: string;
};

export type DeleteExpenseParams = {
  expenseId: string;
};

export type ListExpenseParams = {
  /** 購入日 From（format: yyyy-MM-dd） */
  dateFrom?: string;
  /** 購入日 To（format: yyyy-MM-dd） */
  dateTo?: string;
  labelIds?: string[];
  storeIds?: string[];
  /** 経費名キーワード（部分一致） */
  keyword?: string;
};

/** 経費取得（API4134）レスポンス */
export type ExpenseDetail = {
  expenseId: string;
  purchasedOn: string;
  name: string;
  amount: number;
  quantity: number;
  storeId: string;
  storeName: string;
  labelId: string;
  labelName: string;
  labelColor: string;
  note?: string;
};

/** 経費一覧（API4135）レスポンス項目 */
export type ExpenseListItem = {
  expenseId: string;
  purchasedOn: string;
  name: string;
  amount: number;
  quantity: number;
  /** amount × quantity（BE算出） */
  total: number;
  storeName: string;
  labelId: string;
  labelName: string;
  labelColor: string;
  note?: string;
};

/** 経費名候補（API4141）レスポンス項目 */
export type ExpenseSuggestion = {
  name: string;
  amount: number;
  quantity: number;
  storeId: string;
  storeName: string;
  labelId: string;
  labelName: string;
  labelColor: string;
  note?: string;
};

// --- 型定義（集計）---

export type AggregateExpenseParams = {
  axis: ExpenseAggregateAxis;
  dateFrom?: string;
  dateTo?: string;
  labelIds?: string[];
  storeIds?: string[];
};

export type ExpenseAggregateGroup = {
  /** 軸値。LABEL=labelId / STORE=storeId / MONTH=YYYY-MM / YEAR=YYYY */
  key: string;
  /** 表示名 */
  label: string;
  /** 色（axis=LABEL のみ・任意） */
  color?: string;
  totalAmount: number;
  count: number;
};

export type ExpenseAggregate = {
  axis: ExpenseAggregateAxis;
  totalAmount: number;
  totalCount: number;
  groups: ExpenseAggregateGroup[];
};

// --- 共通ヘッダー ---

const sessionHeaders = () => ({
  'X-Session-Id': localStorage.getItem('session-id'),
});

// --- API関数（品目ラベル）---

/**
 * 品目ラベル一覧取得API（API4111）
 * 論理削除されていない有効なラベルを返す。0件でも空配列。
 */
export const listExpenseLabels = async (): Promise<ExpenseLabel[]> => {
  try {
    const response = await axios.get(KanzhongBackend.LIST_EXPENSE_LABEL, {
      headers: sessionHeaders(),
    });
    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 品目ラベル作成API（API4112）
 * @param params.name - 品目ラベル名（必須, 最大64文字）
 * @param params.color - 色（必須, 最大9文字, HEX）
 * @returns 作成された品目ラベルID
 * @remarks 有効な同名が既存の場合は422。論理削除済み同名は復活（上書き）し ID を返す。
 */
export const createExpenseLabel = async (params: CreateExpenseLabelParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_EXPENSE_LABEL, params, {
      headers: sessionHeaders(),
    });
    return response.data.expenseLabelId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 品目ラベル更新API（API4113）
 * @param params.expenseLabelId - 品目ラベルID（必須）
 * @param params.name - 品目ラベル名（必須, 最大64文字）
 * @param params.color - 色（必須, 最大9文字, HEX）
 * @remarks 有効な同名が既存の場合は422。対象が他人 or 不存在の場合は403/404。
 */
export const updateExpenseLabel = async (params: UpdateExpenseLabelParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_EXPENSE_LABEL, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 品目ラベル削除API（API4114）
 * @param params.expenseLabelId - 品目ラベルID（必須）
 * @remarks 論理削除。既存経費の Chip/表示には残る（BEが結合表示）。
 */
export const deleteExpenseLabel = async (params: DeleteExpenseLabelParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_EXPENSE_LABEL, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（店舗）---

/**
 * 店舗一覧取得API（API4121）
 * 論理削除されていない有効な店舗を返す。0件でも空配列。
 */
export const listExpenseStores = async (): Promise<ExpenseStore[]> => {
  try {
    const response = await axios.get(KanzhongBackend.LIST_EXPENSE_STORE, {
      headers: sessionHeaders(),
    });
    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 店舗作成API（API4122）
 * @param params.name - 店舗名（必須, 最大128文字）
 * @param params.defaultLabelId - デフォルト品目ラベルID（任意）
 * @param params.invoiceNumber - インボイス番号（任意, 最大14文字, T+13桁）
 * @returns 作成された店舗ID
 * @remarks 有効な同名が既存の場合は422。論理削除済み同名は復活（上書き）し ID を返す。
 *          defaultLabelId が不正・不存在・他人・論理削除済みの場合は400。
 */
export const createExpenseStore = async (params: CreateExpenseStoreParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_EXPENSE_STORE, params, {
      headers: sessionHeaders(),
    });
    return response.data.expenseStoreId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 店舗更新API（API4123）
 * @param params.expenseStoreId - 店舗ID（必須）
 * @param params.name - 店舗名（必須, 最大128文字）
 * @param params.defaultLabelId - デフォルト品目ラベルID（任意）
 * @param params.invoiceNumber - インボイス番号（任意, 最大14文字, T+13桁）
 * @remarks 有効な同名が既存の場合は422。対象が他人 or 不存在の場合は403/404。
 */
export const updateExpenseStore = async (params: UpdateExpenseStoreParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_EXPENSE_STORE, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 店舗削除API（API4124）
 * @param params.expenseStoreId - 店舗ID（必須）
 * @remarks 論理削除。既存経費の表示には残る（BEが結合表示）。
 */
export const deleteExpenseStore = async (params: DeleteExpenseStoreParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_EXPENSE_STORE, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（経費）---

/**
 * 経費登録API（API4131）
 * @param params.purchasedOn - 購入日（必須, format: yyyy-MM-dd）
 * @param params.name - 経費名（必須, 最大128文字）
 * @param params.amount - 金額（必須, 0以上の整数）
 * @param params.quantity - 数量（必須, 1以上の整数）
 * @param params.storeId - 店舗ID（必須）
 * @param params.labelId - 品目ラベルID（必須）
 * @param params.note - メモ（任意, 最大1024文字）
 * @returns 作成された経費ID
 * @remarks storeId/labelId が不正・不存在・他人・論理削除済みの場合は400。
 */
export const createExpense = async (params: CreateExpenseParams): Promise<string> => {
  try {
    const response = await axios.post(KanzhongBackend.CREATE_EXPENSE, params, {
      headers: sessionHeaders(),
    });
    return response.data.expenseId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 経費更新API（API4132）
 * @param params.expenseId - 経費ID（必須）
 * @param params.purchasedOn - 購入日（必須, format: yyyy-MM-dd）
 * @param params.name - 経費名（必須, 最大128文字）
 * @param params.amount - 金額（必須, 0以上の整数）
 * @param params.quantity - 数量（必須, 1以上の整数）
 * @param params.storeId - 店舗ID（必須）
 * @param params.labelId - 品目ラベルID（必須）
 * @param params.note - メモ（任意, 最大1024文字）
 * @remarks storeId/labelId が不正の場合は400。対象経費が他人 or 不存在の場合は403/404。
 */
export const updateExpense = async (params: UpdateExpenseParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.UPDATE_EXPENSE, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 経費削除API（API4133）
 * @param params.expenseId - 経費ID（必須）
 * @remarks 物理削除（不可逆）。対象経費が他人 or 不存在の場合は403/404。
 */
export const deleteExpense = async (params: DeleteExpenseParams): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_EXPENSE, params, {
      headers: sessionHeaders(),
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 経費取得API（API4134）
 * @param expenseId - 経費ID（必須）
 * @remarks 対象経費が他人 or 不存在の場合は403/404。
 */
export const getExpense = async (expenseId: string): Promise<ExpenseDetail> => {
  try {
    const response = await axios.get(`${KanzhongBackend.GET_EXPENSE}/${expenseId}`, {
      headers: sessionHeaders(),
    });
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 経費一覧取得API（API4135・ドリルダウン先）
 * @param params.dateFrom - 購入日From（任意, format: yyyy-MM-dd）
 * @param params.dateTo - 購入日To（任意, format: yyyy-MM-dd）
 * @param params.labelIds - 品目ラベルID一覧（任意）
 * @param params.storeIds - 店舗ID一覧（任意）
 * @param params.keyword - 経費名キーワード（任意, 部分一致）
 * @remarks 0件でも空配列。total(=amount×quantity) は BE 算出。
 */
export const listExpenses = async (params: ListExpenseParams): Promise<ExpenseListItem[]> => {
  try {
    const response = await axios.post(KanzhongBackend.LIST_EXPENSE, params, {
      headers: sessionHeaders(),
    });
    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（候補）---

/**
 * 経費名候補検索API（API4141・夢機能プレフィル用）
 * @param keyword - 検索キーワード（必須, 経費名）
 * @param matchType - マッチ種別（任意, PREFIX/SUFFIX）。省略時は前方 OR 後方の両方（BE既定）。
 * @remarks 0件でも空配列。matchType 不正の場合は400。
 */
export const searchExpenseSuggestions = async (
  keyword: string,
  matchType?: ExpenseSuggestionMatchType,
): Promise<ExpenseSuggestion[]> => {
  try {
    const response = await axios.get(KanzhongBackend.SEARCH_EXPENSE_SUGGESTION, {
      headers: sessionHeaders(),
      params: { keyword, ...(matchType ? { matchType } : {}) },
    });
    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

// --- API関数（集計）---

/**
 * 経費集計API（API4142）
 * @param params.axis - 集計軸（必須, LABEL/STORE/MONTH/YEAR）
 * @param params.dateFrom - 購入日From（任意, format: yyyy-MM-dd）
 * @param params.dateTo - 購入日To（任意, format: yyyy-MM-dd）
 * @param params.labelIds - 品目ラベルID一覧（任意）
 * @param params.storeIds - 店舗ID一覧（任意）
 * @remarks axis 不正の場合は400。集計は BE が SUM 済み。
 */
export const aggregateExpense = async (
  params: AggregateExpenseParams,
): Promise<ExpenseAggregate> => {
  try {
    const response = await axios.post(KanzhongBackend.AGGREGATE_EXPENSE, params, {
      headers: sessionHeaders(),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
